// left "Devices" panel

import React, { useState, useMemo, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { PaginationContext } from '../../../__state/Pagination';
import { DataWindowContext } from '../../../__state/DataWindowCollapse';
import { DevicesContext } from '../../../__state/Devices';
import { DeviceEntriesContext } from '../../../__state/DeviceEntries';
import __filter from 'lodash/filter';
import __isEmpty from 'lodash/isEmpty';
import __isEqual from 'lodash/isEqual';
import CircularProgress from '@mui/material/CircularProgress';

import './DeviceFilterView.scss';

import Table from '../../../components/Table';
import RefreshIcon from '@mui/icons-material/Refresh';

const DeviceFilterView = ({
  devices,
  setDevices,
  selectedDevice,
  setSelectedDevice,
  totalDeviceCount,
  setIsResettingDevices,
  tableIndex,
  setTableIndex
}) => {
  const [selectedDeviceIndex, setSelectedDeviceIndex] = useState(-1);
  const [searchedDevicesByText, setSearchedDevicesByText] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortedCols, setSortedCols] = useState([{ id: 'imei', desc: true }]);

  const { setShowFirstEntriesPage } = useContext(PaginationContext);
  const { devicesCollapsed, entriesCollapsed, setDevicesCollapsed, setEntriesCollapsed } =
    useContext(DataWindowContext);
  const { resetCachedDevices } = useContext(DevicesContext);
  const { resetDeviceEntries } = useContext(DeviceEntriesContext);

  // Debounce the searchText input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchText]);

  // Filter devices by IMEI or name
  useEffect(() => {
    if (__isEmpty(debouncedSearch)) {
      setSearchedDevicesByText(devices);
    } else {
      const text = debouncedSearch.toLowerCase();
      const searchedDevices = __filter(devices, (device) => (
        device.imei.toLowerCase().includes(text) ||
        (device.name && device.name.toLowerCase().includes(text))
      ));
      setSearchedDevicesByText(searchedDevices);
    }
  }, [devices, debouncedSearch]);

  // Update index if device list changes
  useEffect(() => {
    const index = searchedDevicesByText.findIndex((device) =>
      __isEqual(device, selectedDevice)
    );
    setSelectedDeviceIndex(index);
  }, [searchedDevicesByText, selectedDevice]);

  // Collapse device window on small screens if both are open
  useEffect(() => {
    if (window.outerWidth < 1440 && !entriesCollapsed && !devicesCollapsed) {
      setDevicesCollapsed(true);
    }
  }, [devicesCollapsed, entriesCollapsed, setDevicesCollapsed]);

  // Default sort if all devices have latestEntryTS
  useEffect(() => {
    if (devices.every((d) => d.latestEntryTS)) {
      setSortedCols([{ id: 'latestWeather', desc: true }]);
    }
  }, [devices]);

  const columns = useMemo(() => [
    {
      Header: 'Device Info',
      id: 'deviceInfo',
      columns: [
        {
          Header: 'IMEI | Alias',
          id: 'imei',
          accessor: (row) => `${row.imei}\n${row.name ? `(${row.name})` : ''}`,
        },
        {
          Header: 'Latest Weather',
          id: 'latestWeather',
          accessor: (row) => row.latestEntryTS || '',
        },
        {
          Header: 'Type',
          id: 'type',
          accessor: 'type',
        },
      ],
    },
  ], []);

  const data = useMemo(() => searchedDevicesByText, [searchedDevicesByText]);

  const toggleCollapsed = () => {
    setDevicesCollapsed(!devicesCollapsed);
    setEntriesCollapsed(true);
  };

  // handle clicking on a device in the left devices panel
  const handleRowClick = (row) => {
    setSelectedDeviceIndex(row.index);
    setSelectedDevice(searchedDevicesByText[row.index]);
    setShowFirstEntriesPage(true);
    setTableIndex(0);
  };

  const handleHeaderClick = (header) => {
    setSortedCols([
      {
        id: header.id,
        desc: !header.isSortedDesc,
      },
    ]);
  };

  const handleReset = async () => {
    setEntriesCollapsed(true);
    setIsResettingDevices(true);

    await Promise.all(
      devices.map((device) => resetDeviceEntries(device.id))
    );

    resetCachedDevices();
    setDevices([]);
    setSelectedDevice({});
  };

  return (
    <div className="device-filter-wrapper" data-collapsed={devicesCollapsed}>
      <div className="collapse-tab" role="button" onClick={toggleCollapsed}>
        <div className="triangle"></div>
      </div>
      <div className="name-tab">Devices</div>
      <div className="filter-inner inner-base">
        <div className="collapse-header">
          <span>Devices</span>
          <div
            className="refresh-data-button"
            role="button"
            onClick={handleReset}
            title="Refresh the entries"
          >
            <RefreshIcon />
          </div>
        </div>
        <div className="filter-controls">
          <div className="search-devices">
            <input
              id="entry-search"
              type="text"
              value={searchText}
              placeholder="Search by IMEI or Alias"
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>
        <div className="filter-table table-wrapper">
          <Table
            columns={columns}
            data={data}
            sortedCols={sortedCols}
            selectedPrimaryRow={selectedDeviceIndex}
            selectedPageSize={100}
            getRowProps={(row) => ({
              key: row.original.id || row.original.imei || row.index,
              onClick: () => handleRowClick(row),
            })}
            getHeaderProps={(header) => ({
              onClick: () => handleHeaderClick(header),
            })}
            getRowId={(row) => row.id || row.imei}
            tableIndex={tableIndex}
            setTableIndex={setTableIndex}
          />
          <div className="table-pagination-wrapper">
            <div className="table-page-info">
              Showing {searchedDevicesByText.length} of {totalDeviceCount} total devices
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

DeviceFilterView.propTypes = {
  devices: PropTypes.array.isRequired,
  totalDeviceCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  selectedDevice: PropTypes.object.isRequired,
  setSelectedDevice: PropTypes.func.isRequired,
  setIsResettingDevices: PropTypes.func.isRequired,
};

export default DeviceFilterView;
